import { Fragment, useEffect, useState } from 'react';
import { useSessao } from '../sessao';
import { api } from '../api';
import { numero } from '../formato';
import { Aviso, Carregando, ErroDaApi, LinkProtegido, Selo } from '../ui';
import { simboloDoPonto, type TipoSimbolo } from '../simbolosDoMapa';

/**
 * O acervo antes do LabManager, no mapa (onda 228; `0224`; RN-209).
 *
 *   «Gostaria de alguma maneira inputar esse banco de dados no
 *    LabManager, para facilitar consultas futuras, principalmente pelo
 *    mapa […]. Talvez seja interessante que esses pontos apareçam no mapa
 *    com outra cor, indicando que foram obtidos antes da criação do
 *    LabManager. Crie um mini dashboard no topo também […].»
 *                                              — Humberto, 30/09/2026
 *
 * Três peças, todas usadas pelo `Mapa.tsx`:
 *
 *   · `PainelDoMapa` — o mini painel do topo: campo, laboratório, metros,
 *     obras, cidades, clientes e clientes ativos, cada número com as duas parcelas
 *     (acervo e LabManager) — somar sem dizer de onde veio apagaria a
 *     diferença entre o que o sistema controlou e o que se leu de um PDF;
 *   · `simbolosDoAcervo` — os símbolos: um por furo que tem coordenada
 *     própria (o desenho do tipo, como os da casa) e, na obra sem furo
 *     georreferenciado, UM por obra, meio transparente quando a posição
 *     veio do endereço («por aqui»). A COR é outra — o azul do acervo —,
 *     e o desenho continua dizendo o tipo;
 *   · `ObraDoAcervo` — o painel ao lado: furos, profundidades, ensaios de
 *     laboratório e os relatórios.
 *
 * O acervo NÃO vai ao Portal do cliente (RN-209): junta obras de todos
 * os clientes, identificados pelo nome escrito na pasta.
 */

export interface PontoDoAcervoNoMapa {
  id: string; codigo: string; tipo: string;
  profundidade: number | null; latitude: number; longitude: number;
}

export interface ObraDoAcervoNoMapa {
  id: string; chave: string; ano: number; numero: string | null;
  data_referencia: string | null; cliente: string; obra_nome: string | null;
  endereco: string | null; municipio: string | null; uf: string | null;
  latitude: number | null; longitude: number | null;
  fonte_coordenada: 'relatorio' | 'furos' | 'locacao' | 'endereco' | 'bairro' | 'municipio' | 'manual' | null;
  pontos_total: number; metros: number; tipos: string[];
  furos_declarados: number | null; metros_declarados: number | null;
  ensaios_lab: number; documentos: number;
  pontos: PontoDoAcervoNoMapa[];
}

export interface RespostaDoAcervo {
  obras: ObraDoAcervoNoMapa[]; total: number; semCoordenada: number;
}

interface Resumo {
  campo: { acervo: number; labmanager: number };
  laboratorio: { acervo: number; labmanager: number };
  metros: { acervo: number; labmanager: number };
  obras: { acervo: number; labmanager: number };
  clientes: { unicos: number; ativos12m: number; acervo: number; labmanager: number };
  cidades: { unicas: number; acervo: number; labmanager: number };
  acervo: { documentos: number; semLocal: number; desde: number | null; ate: number | null };
}

/** Um símbolo do acervo, no formato que o mapa desenha. */
export interface SimboloDoAcervo {
  chave: string;
  obraId: string;
  tipo: TipoSimbolo;
  estimada: boolean;
  nome: string;
  cliente: string;
  latitude: number;
  longitude: number;
}

/* A obra que só teve laboratório vira o frasco; a de campo, o tipo que
   mais aparece nela (SPT antes de rotativa, como a legenda). */
const APROXIMADAS = new Set(['endereco', 'bairro', 'municipio']);

const ORDEM: TipoSimbolo[] = ['sp', 'sr', 'trado', 'cptu', 'dmt', 'vane', 'perm', 'outro'];
function simboloDaObra(o: ObraDoAcervoNoMapa): TipoSimbolo {
  const tipos = new Set(o.tipos.map((t) => simboloDoPonto(t)));
  const t = ORDEM.find((x) => tipos.has(x));
  return t ?? 'lab';
}

export function simbolosDoAcervo(obras: ObraDoAcervoNoMapa[]): SimboloDoAcervo[] {
  const lista: SimboloDoAcervo[] = [];
  for (const o of obras) {
    const nomeObra = o.obra_nome || o.chave.split(/[\\/]/).pop() || o.chave;
    if (o.pontos.length) {
      for (const p of o.pontos) {
        lista.push({
          chave: `acervo:p:${p.id}`, obraId: o.id, tipo: simboloDoPonto(p.tipo),
          estimada: false, nome: `${p.codigo} · ${o.ano}`, cliente: o.cliente,
          latitude: p.latitude, longitude: p.longitude,
        });
      }
    } else if (o.latitude != null && o.longitude != null) {
      lista.push({
        chave: `acervo:o:${o.id}`, obraId: o.id, tipo: simboloDaObra(o),
        estimada: APROXIMADAS.has(o.fonte_coordenada ?? ''),
        nome: `${nomeObra} · ${o.ano}`, cliente: o.cliente,
        latitude: o.latitude, longitude: o.longitude,
      });
    }
  }
  return lista;
}

const mil = (n: number) => numero(n, 0);
/* A data do relatório é de CALENDÁRIO: `new Date('2011-05-10')` é meia-noite
   UTC, que em Brasília ainda é dia 9. Lê-se o texto como está. */
const dia = (s: string) => s.slice(0, 10).split('-').reverse().join('/');

/** Uma casa do painel: o total, e de onde ele veio. */
function Casa({ titulo, total, detalhe, unidade }: {
  titulo: string; total: number; detalhe: string; unidade?: string;
}) {
  return (
    <div className="casa-painel-mapa">
      <span className="rotulo">{titulo}</span>
      <b>{mil(total)}{unidade && <small> {unidade}</small>}</b>
      <small>{detalhe}</small>
    </div>
  );
}

/** O mini painel do topo do mapa. */
export function PainelDoMapa() {
  const [r, setR] = useState<Resumo | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  useEffect(() => {
    let vivo = true;
    api<Resumo>('/acervo/resumo').then((x) => { if (vivo) setR(x); }, (e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, []);
  if (erro) return null;
  if (!r) return <div className="painel-mapa carregando" aria-busy="true" />;
  const parcelas = (a: number, l: number) => `acervo ${mil(a)} · LabManager ${mil(l)}`;
  const desde = r.acervo.desde ?? new Date().getFullYear();
  return (
    <section className="painel-mapa" aria-label="O que a Testesolo já executou">
      <Casa titulo="Ensaios de campo" total={r.campo.acervo + r.campo.labmanager}
        detalhe={parcelas(r.campo.acervo, r.campo.labmanager)} />
      <Casa titulo="Ensaios de laboratório" total={r.laboratorio.acervo + r.laboratorio.labmanager}
        detalhe={parcelas(r.laboratorio.acervo, r.laboratorio.labmanager)} />
      <Casa titulo="Metros perfurados" unidade="m"
        total={Math.round(r.metros.acervo + r.metros.labmanager)}
        detalhe={parcelas(Math.round(r.metros.acervo), Math.round(r.metros.labmanager))} />
      <Casa titulo="Obras" total={r.obras.acervo + r.obras.labmanager}
        detalhe={parcelas(r.obras.acervo, r.obras.labmanager)} />
      <Casa titulo="Cidades atendidas" total={r.cidades.unicas}
        detalhe={parcelas(r.cidades.acervo, r.cidades.labmanager)} />
      <Casa titulo="Clientes atendidos" total={r.clientes.unicos}
        detalhe={`desde ${desde} · únicos pelo nome`} />
      <Casa titulo="Clientes ativos" total={r.clientes.ativos12m}
        detalhe="com obra ou OS nos últimos 12 meses" />
    </section>
  );
}

const TIPO: Record<string, string> = {
  spt: 'SPT', trado: 'Trado', rotativa: 'Rotativa', mista: 'Mista', cpt: 'CPT',
  cptu: 'CPTu', scptu: 'SCPTu', dmt: 'DMT', sdmt: 'SDMT', vane: 'Vane',
  permeabilidade: 'Permeabilidade', poco: 'Poço', outro: 'Outro',
};

const FONTE: Record<string, string> = {
  relatorio: 'coordenada dos furos, lida do relatório',
  furos: 'centro dos furos do relatório',
  locacao: 'posição lida do croqui de locação (ou do KMZ) da pasta da obra',
  endereco: 'localização aproximada — geocodificada do endereço da obra',
  bairro: 'localização aproximada — só o bairro',
  municipio: 'localização aproximada — só a cidade',
  manual: 'posição acertada à mão no mapa',
};

/** A pasta do NAS como o Windows a abre (o navegador não abre `file://` de uma página https). */
export const NAS = '\\\\UNAS-Testesolo\\04_OBRAS\\';
export const caminhoNoNas = (rel: string) => NAS + rel.replace(/\//g, '\\');

/**
 * A coordenada colada: «-26.9194, -49.0661», «-26,9194; -49,0661», o
 * link do Google Maps (`@lat,lon`, `q=lat,lon`, `!3dlat!4dlon`) ou graus,
 * minutos e segundos («26°55'10"S 49°03'58"W»). Fora do Brasil, `null`.
 */
export function lerCoordenada(texto: string): { lat: number; lon: number } | null {
  const t = texto.trim();
  const ok = (lat: number, lon: number) => (
    Number.isFinite(lat) && Number.isFinite(lon) && lat >= -34 && lat <= 6 && lon >= -74 && lon <= -28
      ? { lat: Math.round(lat * 1e7) / 1e7, lon: Math.round(lon * 1e7) / 1e7 } : null);
  let m = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/.exec(t)
    ?? /[@=](-?\d+\.\d+),\s*(-?\d+\.\d+)/.exec(t);
  if (m) return ok(Number(m[1]), Number(m[2]));
  const gms = [...t.matchAll(/(\d+(?:[.,]\d+)?)\s*°\s*(?:(\d+(?:[.,]\d+)?)\s*['′]\s*)?(?:(\d+(?:[.,]\d+)?)\s*["″]\s*)?([NSLOWE])/gi)];
  if (gms.length === 2) {
    const v = gms.map((g) => {
      const n = (x?: string) => Number((x ?? '0').replace(',', '.'));
      const d = n(g[1]) + n(g[2]) / 60 + n(g[3]) / 3600;
      return { d: /[SOW]/i.test(g[4]) ? -d : d, eixo: /[NS]/i.test(g[4]) ? 'lat' : 'lon' };
    });
    const la = v.find((x) => x.eixo === 'lat'); const lo = v.find((x) => x.eixo === 'lon');
    return la && lo ? ok(la.d, lo.d) : null;
  }
  m = /^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/.exec(t)
    ?? /^(-?\d+(?:,\d+)?)\s*[;\s]\s*(-?\d+(?:,\d+)?)$/.exec(t);
  if (m) return ok(Number(m[1].replace(',', '.')), Number(m[2].replace(',', '.')));
  return null;
}

function CopiarNas({ rel, rotulo }: { rel: string; rotulo: string }) {
  const [copiado, setCopiado] = useState(false);
  const caminho = caminhoNoNas(rel);
  return (
    <span className="nas-acervo">
      <code title="Cole na barra do Explorador de Arquivos">{caminho}</code>
      <button type="button" className="botao discreto pequeno"
        onClick={() => {
          navigator.clipboard?.writeText(caminho).then(() => {
            setCopiado(true); setTimeout(() => setCopiado(false), 2000);
          }, () => setCopiado(false));
        }}>
        {copiado ? 'Copiado' : rotulo}
      </button>
    </span>
  );
}

export type EscolherNoMapa = (cb: ((p: { lat: number; lng: number }) => void) | null) => void;

/** Acertar a posição: clicar no mapa ou colar a coordenada, e dizer por quê. */
function AjustarPosicao({ obraId, escolherNoMapa, aoSalvar }: {
  obraId: string; escolherNoMapa?: EscolherNoMapa; aoSalvar(): void;
}) {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    if (!aberto || !escolherNoMapa) return undefined;
    escolherNoMapa((p) => setTexto(`${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}`));
    return () => escolherNoMapa(null);
  }, [aberto, escolherNoMapa]);
  const c = lerCoordenada(texto);
  if (!aberto) {
    return (
      <button type="button" className="botao discreto" onClick={() => setAberto(true)}>
        Ajustar posição
      </button>
    );
  }
  return (
    <form className="ajustar-posicao" onSubmit={(e) => {
      e.preventDefault();
      if (!c || motivo.trim().length < 5) return;
      setSalvando(true); setErro(null);
      api(`/acervo/obras/${obraId}/posicao`, {
        metodo: 'POST', corpo: { latitude: c.lat, longitude: c.lon, motivo: motivo.trim() },
      }).then(() => { setAberto(false); aoSalvar(); }, setErro)
        .finally(() => setSalvando(false));
    }}>
      <p className="sub">
        {escolherNoMapa ? 'Clique no lugar da obra no mapa (o Satélite ajuda), ou cole' : 'Cole'} a
        coordenada ou o link do Google Maps.
      </p>
      <label>Posição
        <input value={texto} onChange={(e) => setTexto(e.target.value)}
          placeholder="-26.919400, -49.066100" aria-invalid={texto !== '' && !c} />
      </label>
      {texto !== '' && !c && <small className="erro-campo">Não reconheci uma coordenada no Brasil.</small>}
      <label>De onde veio
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
          placeholder="croqui de locação do relatório; furo SP-01 na esquina…" />
      </label>
      {erro != null && <ErroDaApi erro={erro} />}
      <div className="linha-botoes">
        <button type="submit" className="botao" disabled={!c || motivo.trim().length < 5 || salvando}>
          {salvando ? 'Salvando…' : 'Salvar posição'}
        </button>
        <button type="button" className="botao discreto" onClick={() => setAberto(false)}>Cancelar</button>
      </div>
    </form>
  );
}

interface Detalhe {
  obra: {
    id: string; chave: string; ano: number; numero: string | null;
    data_referencia: string | null; cliente_nome: string; cliente_cadastro: string | null;
    obra_nome: string | null; endereco: string | null; municipio: string | null; uf: string | null;
    latitude: number | null; longitude: number | null; fonte_coordenada: string | null;
    observacao: string | null;
    furos_declarados: number | null; metros_declarados: number | null;
  };
  pontos: Furo[];
  ensaios: { metodo: string; descricao: string | null; quantidade: number }[];
  documentos: {
    id: string; nome: string; caminho_origem: string; tipo: string;
    paginas: number | null; tem_copia: boolean;
  }[];
}

interface Furo {
  id: string; codigo: string; tipo: string; profundidade: number | null;
  criterio_paralisacao: string | null; paralisacao_texto: string | null;
  metros_solo: number | null; metros_rocha: number | null; topo_rocha: number | null;
  rqd_medio: number | null; recuperacao_media: number | null;
  nivel_agua: number | null; na_24h: number | null; na_seco: boolean; na_data: string | null;
  cota: number | null; data_inicio: string | null; data_fim: string | null;
  data_fonte: 'boletim' | 'relatorio' | null;
  latitude: number | null; longitude: number | null; datum: string | null; fuso: number | null;
  ensaios: { ensaio: string; quantidade: number | null; metros: number | null }[];
  camadas: { topo: number; base: number; descricao: string; origem: string | null }[];
  spt: { profundidade: number; n: number | null; n_texto: string | null }[];
}

const PARADA: Record<string, string> = {
  impenetravel_amostrador: 'Impenetrável ao amostrador',
  impenetravel_lavagem: 'Impenetrável à lavagem',
  impenetravel_trado: 'Impenetrável ao trado',
  limite_sondagem: 'Limite da sondagem',
  solicitacao_contratante: 'Solicitação da contratante',
  outro: 'Outro',
};
const PARADA_CURTA: Record<string, string> = {
  impenetravel_amostrador: 'impenetrável', impenetravel_lavagem: 'impenetrável (lav.)',
  impenetravel_trado: 'impenetrável (trado)', limite_sondagem: 'limite',
  solicitacao_contratante: 'contratante', outro: 'outro',
};
const ENSAIO: Record<string, string> = {
  spt: 'SPT', trado: 'Trado', lavagem: 'Lavagem', rotativa: 'Rotativa', cptu: 'CPTu',
  dissipacao: 'Dissipação', infiltracao: 'Infiltração', permeabilidade: 'Permeabilidade',
  vane: 'Vane', dmt: 'DMT', shelby: 'Shelby', denison: 'Denison', bloco: 'Bloco',
  deformada: 'Deformada', outro: 'Outro',
};
const ORIGEM: Record<string, string> = {
  aterro: 'aterro', aluvionar: 'aluvionar', coluvionar: 'coluvionar', fluvial: 'fluvial', residual: 'residual',
  sedimentar: 'sedimentar', marinho: 'marinho', organico: 'orgânico',
  rocha_alterada: 'rocha alterada', rocha: 'rocha', outro: 'outro',
};
const m2 = (v: number | null) => (v == null ? '—' : numero(v, 2));

/** O N do SPT metro a metro, em barras — a leitura que se faz no boletim. */
function GraficoSpt({ spt, na }: { spt: Furo['spt']; na: number | null }) {
  if (!spt.length) return null;
  const fundo = Math.max(...spt.map((x) => x.profundidade)) + 1;
  const passo = 14; const larg = 220; const esq = 44; const nMax = 50;
  const alt = Math.ceil(fundo) * passo + 20;
  const x = (n: number) => esq + Math.min(n, nMax) / nMax * (larg - esq - 8);
  return (
    <svg className="grafico-spt" viewBox={`0 0 ${larg} ${alt}`} role="img"
      aria-label={`N do SPT por profundidade, até ${numero(fundo - 1, 2)} m`}>
      {[0, 10, 20, 30, 40, 50].map((n) => (
        <g key={n}>
          <line x1={x(n)} x2={x(n)} y1={12} y2={alt - 4} className="grade" />
          <text x={x(n)} y={9} textAnchor="middle" className="eixo">{n}</text>
        </g>
      ))}
      {spt.map((s) => {
        const y = 14 + s.profundidade * passo;
        const n = s.n ?? nMax;
        return (
          <g key={s.profundidade}>
            <text x={esq - 6} y={y + 9} textAnchor="end" className="eixo">{numero(s.profundidade, 0)}</text>
            <rect x={esq} y={y} width={Math.max(1, x(n) - esq)} height={passo - 4}
              className={s.n == null ? 'barra-n fracao' : 'barra-n'} />
            {/* Perto da borda, o número entra na barra para não ser cortado. */}
            {x(n) > larg - 34
              ? <text x={x(n) - 3} y={y + 9} textAnchor="end" className="valor dentro">{s.n ?? s.n_texto}</text>
              : <text x={x(n) + 3} y={y + 9} className="valor">{s.n ?? s.n_texto}</text>}
          </g>
        );
      })}
      {na != null && (
        <g>
          <line x1={esq} x2={larg - 4} y1={14 + na * passo} y2={14 + na * passo} className="linha-na" />
          <text x={larg - 4} y={14 + na * passo - 2} textAnchor="end" className="valor">NA</text>
        </g>
      )}
    </svg>
  );
}

/** O perfil de um furo: o que o boletim disse além da profundidade. */
function PerfilDoFuro({ f }: { f: Furo }) {
  const data = f.data_inicio || f.data_fim
    ? `${f.data_inicio ? dia(f.data_inicio) : '?'}${f.data_fim && f.data_fim !== f.data_inicio ? ` a ${dia(f.data_fim)}` : ''}`
      + (f.data_fonte === 'relatorio' ? ' (data do relatório)' : '')
    : null;
  const na = f.na_seco ? 'seco' : f.nivel_agua != null
    ? `${m2(f.nivel_agua)} m${f.na_24h != null ? ` · 24 h: ${m2(f.na_24h)} m` : ''}` : null;
  return (
    <div className="perfil-furo">
      <dl>
        {f.criterio_paralisacao && (
          <><dt>Paralisação</dt><dd>{PARADA[f.criterio_paralisacao]}
            {f.paralisacao_texto && <small> — {f.paralisacao_texto}</small>}</dd></>
        )}
        {na && <><dt>Nível d’água</dt><dd>{na}{f.na_data && <small> ({dia(f.na_data)})</small>}</dd></>}
        {(f.metros_solo != null || f.metros_rocha != null) && (
          <><dt>Solo × rocha</dt><dd>{m2(f.metros_solo)} m em solo · {m2(f.metros_rocha)} m em rocha/alteração</dd></>
        )}
        {f.topo_rocha != null && <><dt>Topo rochoso</dt><dd>{m2(f.topo_rocha)} m</dd></>}
        {(f.rqd_medio != null || f.recuperacao_media != null) && (
          <><dt>RQD · recuperação</dt><dd>{f.rqd_medio == null ? '—' : `${numero(f.rqd_medio, 0)} %`} · {f.recuperacao_media == null ? '—' : `${numero(f.recuperacao_media, 0)} %`}</dd></>
        )}
        {f.cota != null && <><dt>Cota da boca</dt><dd>{numero(f.cota, 3)} m</dd></>}
        {data && <><dt>Execução</dt><dd>{data}</dd></>}
        {f.latitude != null && (
          <><dt>Coordenada</dt><dd>{numero(f.latitude, 6)}, {numero(f.longitude, 6)}
            {f.datum && <small> · {f.datum}{f.fuso ? ` ${f.fuso}S` : ''}</small>}</dd></>
        )}
        {f.ensaios.length > 0 && (
          <><dt>Ensaios</dt><dd>{f.ensaios.map((e) => `${ENSAIO[e.ensaio] ?? e.ensaio}`
            + `${e.quantidade != null ? ` ${e.quantidade}` : ''}${e.metros != null ? ` (${m2(e.metros)} m)` : ''}`).join(' · ')}</dd></>
        )}
      </dl>
      {f.camadas.length > 0 && (
        <table className="tabela-acervo camadas">
          <thead><tr><th className="num">De (m)</th><th className="num">Até (m)</th><th>Camada</th></tr></thead>
          <tbody>
            {f.camadas.map((c) => (
              <tr key={c.topo}>
                <td className="num">{m2(c.topo)}</td><td className="num">{m2(c.base)}</td>
                <td>{c.descricao}{c.origem && <small> · {ORIGEM[c.origem] ?? c.origem}</small>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <GraficoSpt spt={f.spt} na={f.na_seco ? null : f.nivel_agua} />
      {!f.camadas.length && !f.spt.length && !f.criterio_paralisacao && !na && (
        <p className="sub">O relatório deste furo não trouxe mais que a profundidade.</p>
      )}
    </div>
  );
}

/** O painel ao lado do mapa, para uma obra do acervo. */
export function ObraDoAcervo({ id, aoFechar, escolherNoMapa, aoMudarPosicao }: {
  id: string; aoFechar(): void;
  /** O mapa entrega o próximo clique a quem pede (ajustar posição); `null` devolve. */
  escolherNoMapa?: EscolherNoMapa;
  aoMudarPosicao?(): void;
}) {
  const { pode, eu } = useSessao();
  const [versao, setVersao] = useState(0);
  const [d, setD] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setD(null); setErro(null); setAberto(null);
    api<Detalhe>(`/acervo/obras/${id}`).then((x) => { if (vivo) setD(x); },
      (e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [id, versao]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando />;
  const o = d.obra;
  const metros = d.pontos.reduce((s, p) => s + (p.profundidade ?? 0), 0);
  const aproximada = APROXIMADAS.has(o.fonte_coordenada ?? '');
  /*
   * Ver não é baixar (onda 550, `0527`): «Sim, mas é possível configurar no
   * perfil de cada usuário» (Humberto, 22/10). Quem vê o acervo e não tem
   * «Baixar documentos do acervo» continua vendo QUAIS relatórios a obra
   * tem — o nome, as páginas, o caminho do NAS —, sem o link do PDF, e a
   * tela diz por quê uma vez, em cima da lista (P-UI-24: sumir sem dizer
   * seria «isto não existe»). A recusa de verdade é a da API.
   */
  const baixa = pode('acervo.baixar') && pode('mapa.ver');
  const temCopia = d.documentos.some((x) => x.tem_copia);

  return (
    <div className="lugar lugar-acervo">
      <div className="linha-topo">
        <div className="cresce">
          <h3>{o.obra_nome || o.chave.split(/[\\/]/).pop()}</h3>
          <p className="sub">
            {o.cliente_cadastro ?? o.cliente_nome}
            {o.endereco && ` · ${o.endereco}`}
            {o.municipio && ` · ${o.municipio}/${o.uf ?? ''}`}
          </p>
        </div>
        <button type="button" className="botao discreto" onClick={aoFechar}>
          Todos os lugares
        </button>
      </div>
      <p className="selos-acervo">
        <Selo tom="neutro" titulo="Executado antes do LabManager — lido dos relatórios da época">
          Acervo {o.ano}
        </Selo>
        {o.numero && <Selo tom="neutro">nº {o.numero}</Selo>}
        {o.data_referencia && <Selo tom="neutro">{dia(o.data_referencia)}</Selo>}
      </p>
      {o.fonte_coordenada && (
        <p className="sub">{FONTE[o.fonte_coordenada] ?? o.fonte_coordenada}</p>
      )}
      {aproximada && (
        <Aviso tipo="atencao" titulo="Localização aproximada">
          O relatório não trazia a coordenada dos furos: o símbolo marca o
          endereço da obra, não o furo.
        </Aviso>
      )}
      {pode('acervo.importar') && (
        <AjustarPosicao obraId={o.id} escolherNoMapa={escolherNoMapa}
          aoSalvar={() => { setVersao((v) => v + 1); aoMudarPosicao?.(); }} />
      )}
      <p className="sub">Pasta no NAS: <CopiarNas rel={o.chave} rotulo="Copiar caminho" /></p>
      {(o.furos_declarados != null || o.metros_declarados != null)
        && (d.pontos.length < (o.furos_declarados ?? 0) || metros + 0.05 < (o.metros_declarados ?? 0)) && (
        <p className="sub declarado-acervo">
          O relatório declara {o.furos_declarados != null && `${o.furos_declarados} furo${o.furos_declarados === 1 ? '' : 's'}`}
          {o.furos_declarados != null && o.metros_declarados != null && ', '}
          {o.metros_declarados != null && `${numero(o.metros_declarados, 2)} m de perfuração`}
          {d.pontos.length
            ? ` — ${d.pontos.length} furo${d.pontos.length === 1 ? '' : 's'} lido${d.pontos.length === 1 ? '' : 's'} um a um.`
            : ' — os boletins são desenho e não se leem furo a furo.'}
        </p>
      )}

      {d.pontos.length > 0 && (
        <section>
          <h4>Ensaios de campo · {d.pontos.length} · {numero(metros, 2)} m</h4>
          <table className="tabela-acervo">
            <thead>
              <tr><th>Furo</th><th>Tipo</th><th className="num">Prof. (m)</th><th className="num">NA (m)</th><th>Parada</th></tr>
            </thead>
            <tbody>
              {d.pontos.map((p) => (
                <Fragment key={p.id}>
                  <tr className={`linha-furo${aberto === p.id ? ' aberta' : ''}`}
                    onClick={() => setAberto(aberto === p.id ? null : p.id)}>
                    <td>
                      <button type="button" className="como-link" aria-expanded={aberto === p.id}
                        onClick={(e) => { e.stopPropagation(); setAberto(aberto === p.id ? null : p.id); }}>
                        {p.codigo}
                      </button>
                    </td>
                    <td>{TIPO[p.tipo] ?? p.tipo}</td>
                    <td className="num">{m2(p.profundidade)}</td>
                    <td className="num">{p.na_seco ? 'seco' : m2(p.nivel_agua)}</td>
                    <td>{p.criterio_paralisacao ? PARADA_CURTA[p.criterio_paralisacao] : '—'}</td>
                  </tr>
                  {aberto === p.id && (
                    <tr className="detalhe-furo"><td colSpan={5}><PerfilDoFuro f={p} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {d.ensaios.length > 0 && (
        <section>
          <h4>Ensaios de laboratório · {mil(d.ensaios.reduce((s, e) => s + e.quantidade, 0))}</h4>
          <ul className="lista-mapa">
            {d.ensaios.map((e) => (
              <li key={e.metodo}>
                <div className="lista-mapa-cabeca">
                  <b>{e.metodo}</b>{e.descricao && <span>{e.descricao}</span>}
                  <Selo tom="neutro">{mil(e.quantidade)}</Selo>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h4>Relatórios</h4>
        {!baixa && temCopia && (
          <p className="sub sem-baixar" role="note">
            {eu?.atuandoPor
              ? `Você está atuando como ${eu.nome ?? 'uma persona'}, que não baixa os documentos do acervo. `
                + 'Volte a ser você na tarja do topo.'
              : 'Os PDFs não abrem com o seu acesso: falta «Baixar documentos do acervo». '
                + 'Peça a quem administra os usuários.'}
          </p>
        )}
        {d.documentos.length === 0 ? <p className="sub">Nenhum PDF indexado.</p> : (
          <ul className="lista-mapa">
            {d.documentos.map((doc) => (
              <li key={doc.id}>
                <div className="lista-mapa-cabeca">
                  {doc.tem_copia && baixa
                    ? (
                      <LinkProtegido caminho={`/acervo/documentos/${doc.id}/pdf`} nomeQueda={doc.nome}>
                        {doc.nome}
                      </LinkProtegido>
                    )
                    : <b>{doc.nome}</b>}
                  {doc.paginas && <small>{doc.paginas} p.</small>}
                </div>
                {/* Sem a cópia aqui, ou sem poder baixá-la: o original está no NAS. */}
                {(!doc.tem_copia || !baixa) && (
                  <small>no NAS: <CopiarNas rel={doc.caminho_origem} rotulo="Copiar" /></small>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
